import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useToast } from '@chakra-ui/react';

import * as actions from '@/actions';
import { POINTS_CANNOT_BE_NEGATIVE_403 } from '@/utils/commonParameters';
import { ApiMessagesConstants } from '@/constants/apiMessagesConstants';
import { API_RETURN_CODE } from '@Constants/apiReturnCode';
import { parseBlobData, errorNotification } from '@Utils/commonFunction';
import { useClearAllStates } from '@Hooks/useClearAllStates';

import { useErrorHandlerContext } from '@Components/errorHandler';
import { RenderedToast } from '@Components/toast';
import { popupToast } from '@Utils/toast';

export const useApiResponseHandler = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const { setErrorStatusCode, setMsg } = useErrorHandlerContext();
  const { clearAllStates } = useClearAllStates();

  function getErrorToast(id, title, duration = 10 * 1000) {
    return toast({
      id,
      status: 'error',
      position: 'top',
      duration,
      isClosable: true,
      render: ({ id, onClose }) => (
        <RenderedToast id={id} onClose={onClose} status="error" title={title} />
      ),
    });
  }

  function handle400ErrorHandler() {
    popupToast({
      toast,
      title: 'Bad Request 格式錯誤',
      status: 'error',
    });
  }

  function handle403ErrorHandler(data) {
    const { msg, returnCode } = data;

    if (
      returnCode === API_RETURN_CODE.admin.tokenExpired ||
      returnCode === API_RETURN_CODE.module.notFound
    ) {
      clearAllStates();
      errorNotification();
      return;
    }

    if (msg === ApiMessagesConstants.UNAUTHORIZED) {
      dispatch(actions.getProfileAction.getProfile());
      return;
    }

    if (msg === POINTS_CANNOT_BE_NEGATIVE_403) {
      if (!toast.isActive(POINTS_CANNOT_BE_NEGATIVE_403)) {
        getErrorToast(
          POINTS_CANNOT_BE_NEGATIVE_403,
          t('common.pointsCannotBeNegative403ErrorMessage'),
        );
      }
    }

    if (msg === ApiMessagesConstants.PLAN_EXPIRED) {
      dispatch(actions.getProfileAction.getProfile());

      if (!toast.isActive(ApiMessagesConstants.PLAN_EXPIRED)) {
        getErrorToast(ApiMessagesConstants.PLAN_EXPIRED, t('common.expirationMessage'), 100 * 1000);
      }
    }
  }

  function handle404ErrorHandler(data) {
    const { msg } = data;

    if (msg === ApiMessagesConstants.ADMIN_NOT_FOUND) {
      if (!toast.isActive(ApiMessagesConstants.ADMIN_NOT_FOUND)) {
        getErrorToast(ApiMessagesConstants.ADMIN_NOT_FOUND, t('common.adminNotFoun'));
      }

      dispatch(actions.logout());
    }
  }

  const handler = async (promise) => {
    setErrorStatusCode(200);
    try {
      const { status, data, config } = await promise;

      if (!status || status >= 500 || !config) {
        setErrorStatusCode(500);
        return;
      }

      const { systemTimestamp, msg, timestamp } = data;

      setErrorStatusCode(status);
      setMsg(msg);

      // TODO: 待確認 parseBlobData can return value
      const responseData = config?.responseType === 'blob' ? parseBlobData(data) : data;

      if (systemTimestamp || timestamp) {
        dispatch(actions.setSystemTimestamp(systemTimestamp || timestamp || null));
      }

      // 維護中
      if (responseData?.returnCode === API_RETURN_CODE.module.maintenance) {
        history.push('/maintenance');
      }

      if (
        msg === 'Event not found' ||
        msg === ApiMessagesConstants.PROJECT_NOT_FOUND ||
        msg === ApiMessagesConstants.ADMIN_PROJECT_NOT_FOUND
      ) {
        if (!toast.isActive(ApiMessagesConstants.PROJECT_NOT_FOUND)) {
          getErrorToast(
            ApiMessagesConstants.PROJECT_NOT_FOUND,
            t('common.eventNotFound'),
            3 * 1000,
          );
        }

        history.push('/');
      }

      if (status === 400) {
        handle400ErrorHandler();
      }

      if (status === 401) {
        clearAllStates();
        errorNotification();
      }

      if (status === 403) {
        handle403ErrorHandler(responseData);
      }

      if (status === 404) {
        handle404ErrorHandler(responseData);
      }

      return data;
    } catch {
      setErrorStatusCode(500);
    }
  };

  return handler;
};
